import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppointmentService from "../../api/AppointmentService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { CustomTextArea } from "../../components/CustomTextArea/CustomTextArea";
import { CustomToggleSelect } from "../../components/CustomToggleSelect/CustomToggleSelect";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import Modal from "../../components/Modal/Modal";
import PinCropMap from "../../components/PinCropMap/PinCropMap";
import { MonitorIcon } from "../../icons/MonitorIcon/MonitorIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { RootState } from "../../store";
import { adjustPinActions } from "../../store/adjust-pin-slice";
import { oldMonitorsActions } from "../../store/old-monitors-slice";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/monitor-adjust-position.module.css";
import { InfestationType } from "../../types/service";

export const MonitorAdjustPositionData = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const monitor = useSelector((state: RootState) => state.monitor.monitor);
  const appointment = useSelector((state: RootState) => state.appointment);
  const {
    monitorPosition,
    position,
    pinName,
    location,
    isWireless,
    wirelessId,
    trapType,
  } = useSelector((state: RootState) => state.adjustPin);
  const monitorTypes = useSelector(
    (state: RootState) => state.monitor.monitorTypes
  );

  const [textArea, setTextArea] = useState<string>("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<InfestationType>(
    trapType ?? "baitEaten"
  );

  const pinAbbr = {
    name: pinName,
    number:
      position != null
        ? monitor.isTemporary
          ? "T"
          : "" + position.toString()
        : "001",
  };

  const adjustmentStep = useSelector(
    (state: RootState) => state.tabs.adjustment.step
  );

  const handleBack = () => {
    if (adjustmentStep === 1) {
      navigate(-1);
    } else {
      dispatch(activeTabsActions.setAdjustmentStep(1));
    }
  };

  const setLocation = (location: string) => {
    dispatch(adjustPinActions.setLocation(location));
  };

  const setPosition = (number: string) => {
    dispatch(adjustPinActions.setPosition(number));
  };

  const saveChanges = async () => {
    const currentTime = new Date().toISOString();

    dispatch(
      oldMonitorsActions.setOldMonitors({
        id: monitor.id,
        position: monitor.position.toString(),
        location: monitor.location,
        appointmentId: appointment.id,
        createdAt: currentTime,
        action: "adjusted",
        isWireless: monitor.isWireless,
        wirelessId: monitor.wirelessId,
        trapType: monitor.trapType,
      })
    );

    setLoading(true);

    await AppointmentService.UpdateMonitorDetails(appointment.id, {
      monitor_id: monitor.id,
      position,
      location,
      reason: textArea,
      mapPosition: {
        mapVersionId: null,
        mapPoint: {
          ...monitorPosition,
        },
      },
      is_active: true,
      is_wireless: isWireless,
      wireless_id: wirelessId,
      trap_type: activeTab,
    })
      .then(() => {
        dispatch(adjustPinActions.resetState());
        dispatch(activeTabsActions.setAdjustmentStep(1));
        navigate(`/properties/${appointment.id}/show`);
      })
      .catch((error) => {
        setIsModalOpen(false);
        dispatch(adjustPinActions.setIsWireless(monitor.isWireless));
        dispatch(
          adjustPinActions.setWirelessId({ wirelessId: monitor.wirelessId })
        );
        dispatch(activeTabsActions.setAdjustmentStep(1));
        setIsErrorModalOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTextareaChange = (note: string) => {
    setTextArea(note);
  };

  const handleFirstContinueClick = () => {
    dispatch(activeTabsActions.setAdjustmentStep(2));
  };

  const handleDigitalMonitorSetup = () => {
    if (monitor.isWireless === false)
      dispatch(adjustPinActions.setIsWireless(true));

    navigate("/monitors/create/step-4", { state: { editMode: true } });
  };

  const handleAdjustPosition = () => {
    if (monitorTypes.map != null) {
      dispatch(
        adjustPinActions.setMonitorPosition({
          x: monitor.x ?? 1,
          y: monitor.y ?? 1,
        })
      );
      dispatch(adjustPinActions.setPosition(monitor.position ?? ""));
      dispatch(
        adjustPinActions.setPinName(
          monitor?.monitorType.slice(0, 3).toUpperCase() ?? ""
        )
      );
      dispatch(adjustPinActions.setLocation(monitor.location));
      navigate("/monitor/adjust-position");
    } else setError(() => t("PropertyAreaMapMissing"));
  };

  const changeActiveTab = (value: InfestationType) => {
    setActiveTab(value);
    dispatch(activeTabsActions.setIssuesTab(value));
    dispatch(adjustPinActions.setTrapType(value));
  };

  return (
    <AppLayout hideHeader>
      <Modal
        isOpen={isErrorModalOpen}
        message={t("SomethingWentWrong")}
        note={"CannotConfigureMonitor"}
        onConfirm={() => {
          setIsErrorModalOpen(false);
        }}
        confirmText={"Understood"}
        iconType={"alert"}
        confirmButtonColor={"#3D5170"}
      />

      <Modal
        isOpen={isModalOpen}
        message={t("AreYouSureEditMonitor")}
        note={"EditMonitorNote"}
        iconType="monitor"
        confirmText="Confirm"
        cancelText="Cancel"
        noteColor="#8493AB"
        loading={loading}
        onConfirm={saveChanges}
        onCancel={() => {
          setIsModalOpen(false);
        }}
      />
      <BackButtonHeader
        handleBackButtonClicked={handleBack}
        showDocumentation={false}
      />
      <AppointmentDetails />
      {adjustmentStep === 1 ? (
        <div className={styles["content-wrapper"]}>
          <div className={styles["content"]}>
            <p className={styles["monitor-adjust--title"]}>
              <MonitorIcon color="dark" /> {monitor?.monitorType} {position}
            </p>

            <div
              onClick={handleAdjustPosition}
              className={`${styles["monitor-adjust--map"]} ${styles["monitor-adjust-data--map"]}`}
            >
              <PinCropMap
                map={monitorTypes.map!}
                crop={monitorPosition}
                onCropChange={() => {}}
                monitorPinAbbr={pinAbbr}
                zoom={false}
                isDraggable={false}
              />
            </div>

            {error && <div className={styles["error-message"]}>{error}</div>}

            <CustomInput
              label="Standortinformation"
              placeholder="Kein Standort eingetragen"
              onInputChange={setLocation}
              onBlur={() => {}}
              initialValue={location}
              maxLength={60}
              hideTick
              editMonitor
            />

            <CustomInput
              label="Monitor Nummer"
              placeholder="Kein Nummer eingetragen"
              onInputChange={setPosition}
              onBlur={() => {}}
              initialValue={position.toString()}
              maxLength={10}
              hideTick
              numeric={true}
              editMonitor
            />

            <div className={styles["monitor-adjust__issue-type"]}>
              <p className={styles["monitor-adjust__issue-type--title"]}>
                {t("TypeIssue")}
              </p>

              <div className={styles["monitor-adjust__issue-type__buttons"]}>
                <div
                  className={`${styles["monitor-adjust__issue-type__button"]} ${
                    activeTab === "baitEaten" &&
                    styles["monitor-adjust__issue-type__button--active"]
                  }`}
                  onClick={() => changeActiveTab("baitEaten")}
                >
                  {t("baitEaten")}
                </div>

                <div
                  className={`${styles["monitor-adjust__issue-type__button"]} ${
                    activeTab === "animalFound" &&
                    styles["monitor-adjust__issue-type__button--active"]
                  }`}
                  onClick={() => changeActiveTab("animalFound")}
                >
                  {t("animalFound")}
                </div>
              </div>
            </div>

            {wirelessId ? (
              <CustomInput
                label="Digitaler Monitor"
                placeholder="Kennnummer manuell eintragen"
                initialValue={wirelessId}
                maxLength={60}
                onClick={handleDigitalMonitorSetup}
                hideTick
                editMonitor
              />
            ) : (
              <CustomToggleSelect
                selected={isWireless}
                backgroundColor="white"
                onClick={handleDigitalMonitorSetup}
              >
                Digitaler Monitor
              </CustomToggleSelect>
            )}
          </div>
          <FloatingButtons>
            <CustomButton
              color="gray"
              onClick={handleFirstContinueClick}
              width={80}
            >
              {t("Confirm")}
            </CustomButton>
          </FloatingButtons>
        </div>
      ) : (
        <div className={styles["content-wrapper"]}>
          <div className={styles["content"]}>
            <span className={styles["monitor-adjust--title"]}>
              {t("AdjustMonitor")}
            </span>

            <CustomTextArea
              placeholder={t("MovingMonitorReason")}
              onTextareaChange={handleTextareaChange}
              defaultValue={textArea}
            />
          </div>
          <FloatingButtons>
            <CustomButton
              color="gray"
              width={80}
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              {t("Continue")}
            </CustomButton>
          </FloatingButtons>
        </div>
      )}
    </AppLayout>
  );
};
